// Возврат фокуса после закрытия слоя на элемент, который его открыл (README «Доступность»).
// Radix возвращает фокус только на свой Trigger; окна сервиса открываются и кодом (из меню,
// из строки таблицы), поэтому опору запоминаем сами в момент открытия. Если, пока слой
// закрывался, страница сама перевела фокус (переход из ленты уведомлений на строку заказа),
// его не перебиваем: возврат — только когда фокус ушёл вместе со слоем.
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

const OTHER_LAYERS =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]';

export interface ReturnFocusOptions {
  /** Явное место возврата: кнопка «Создать ▾», если окно открыли из её меню. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Пока слой открыт, фокус снаружи меняет место возврата (боковая панель без затемнения). */
  trackWhileOpen?: boolean;
}

/**
 * Фокус уже на странице, а не в закрытом слое и не потерян (на body): его перевёл код страницы,
 * и возврат на кнопку слоя его бы перебил.
 */
export function focusMovedOn(content?: HTMLElement | null): boolean {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body && !content?.contains(active);
}

export function useReturnFocus(
  open: boolean,
  contentRef: RefObject<HTMLElement | null>,
  { returnFocusRef, trackWhileOpen = false }: ReturnFocusOptions = {},
): (event: Event) => void {
  const openerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const remember = (element: EventTarget | null) => {
      if (
        element instanceof HTMLElement &&
        element !== document.body &&
        !contentRef.current?.contains(element)
      ) {
        openerRef.current = element;
      }
    };
    remember(document.activeElement);
    if (!trackWhileOpen) return undefined;
    // Фокус в других слоях (окно подтверждения, меню, подсказка) место возврата не меняет.
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(OTHER_LAYERS)) return;
      remember(target);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open, trackWhileOpen, contentRef]);

  return useCallback(
    (event: Event) => {
      event.preventDefault();
      if (focusMovedOn(contentRef.current)) return;
      const target = returnFocusRef?.current ?? openerRef.current;
      if (target?.isConnected) target.focus();
    },
    [returnFocusRef, contentRef],
  );
}
